"use client";

import { MoreHorizontal } from "lucide-react";
import { cn } from "@/lib/utils";
import NotificationMenu from "./NotificationMenu";
import React, { useState } from "react";
import { useI18N } from "common/hooks";
import VenuePlaceholderImage from "@/components/common/VenuePlaceholderImage";

// ============================================================================
// Types
// ============================================================================

export interface NotificationContent {
  prefix: string;
  suffix: string;
}

export interface NotificationItemData {
  id: string;
  type: "live_alert" | "new_info";
  isUnread: boolean;
  isHighlighted: boolean;
  images: string[];
  content: NotificationContent;
  timestamp: string;
  createdAt: string;
  readableTimestamp: string;
  slug?: string;
  hasVenuePhoto?: boolean;
  isPlaceholder?: boolean;
}

export interface NotificationItemProps {
  data: NotificationItemData;
  onClick: () => void;
  onMenuClick?: () => void;
  onMarkAsRead?: (id: string) => void;
}

// ============================================================================
// Main Component
// ============================================================================

// Accessibility Note: This component uses proper button semantics throughout
// - Main notification is a semantic button with descriptive content
// - Nested menu button has aria-label and uses stopPropagation to prevent conflicts
// - Images use empty alt="" as they are decorative (venue images)
// - Time element has dateTime and aria-label for screen readers
// - Unread state announced via sr-only text
export function NotificationItem({
  data,
  onClick,
  onMenuClick,
  onMarkAsRead,
}: NotificationItemProps) {
  const i18n = useI18N();
  const {
    isUnread,
    isHighlighted,
    images,
    content,
    timestamp,
    createdAt,
    readableTimestamp,
    id,
    hasVenuePhoto = false,
    isPlaceholder = false,
  } = data;
  const hasStackedImages = images.length > 1;

  const handleMainClick = (e: React.MouseEvent) => {
    // Don't trigger if clicking on the menu area
    const target = e.target as HTMLElement;
    const isMenuClick = target.closest('[data-menu-area]');
    
    if (isMenuClick) {
      return;
    }
    onClick();
  };

  return (
    <button
      type="button"
      onClick={handleMainClick}
      className={cn(
        "w-full flex items-center gap-4 p-3 text-left transition-colors focus-blue",
        "hover:bg-bg-brand-ghost-hover",
        isHighlighted
          ? "bg-container-brand-minimal"
          : "bg-bg-brand-secondary-default",
      )}
    >
      {/* Left Section: Unread Indicator + Images */}
      <div className="flex items-center gap-2 shrink-0">
        {/* Unread Indicator */}
        <div
          className={cn(
            "w-2 h-2 rounded-full shrink-0",
            isUnread ? "bg-bg-brand-primary-default" : "bg-transparent",
          )}
          aria-hidden="true"
        />

        {/* Images */}
        {isPlaceholder ? (
          <VenuePlaceholderImage className="w-14 h-14 rounded-lg shrink-0" />
        ) : hasStackedImages ? (
          <StackedImages images={images} hasVenuePhoto={hasVenuePhoto} />
        ) : (
          <SingleImage src={images[0]} hasVenuePhoto={hasVenuePhoto} />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className="label-m-medium text-fg-neutral-strong leading-5">
          {isUnread && <span className="sr-only">{i18n.get("unreadPrefix")}</span>}
          {data.type === "live_alert" ? (
            <>
              <span className="label-m-semibold">{content.prefix}</span> for{" "}
              <span className="label-m-semibold"> {content.suffix}</span>
            </>
          ) : (
            <>
              {content.prefix} for{" "}
              <span className="label-m-semibold"> {content.suffix}</span>
            </>
          )}
        </p>
      </div>

      {/* Right Section: Time + Menu */}
      <div 
        className="flex flex-col items-center justify-center w-11 shrink-0"
        data-menu-area="true"
      >
        <time dateTime={createdAt} className="label-s-medium text-fg-neutral-strong text-center">
          <span aria-hidden="true">{timestamp}</span>
          <span className="sr-only">{readableTimestamp}</span>
        </time>
        {onMarkAsRead ? (
          <NotificationMenu
            notificationId={id}
            isUnread={isUnread}
            onMarkAsRead={onMarkAsRead}
          />
        ) : (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onMenuClick?.();
            }}
            className="p-1 rounded-md hover:border border-border-token-neutral-minimal transition-colors focus-blue"
            aria-label={i18n.get("moreOptions")}
          >
            <MoreHorizontal size={24} className="text-fg-neutral-strong" aria-hidden="true" />
          </button>
        )}
      </div>
    </button>
  );
}

// ============================================================================
// Sub-components
// ============================================================================

interface SingleImageProps {
  src: string;
  hasVenuePhoto?: boolean;
}

function SingleImage({ src, hasVenuePhoto = false }: SingleImageProps) {
  const [hasError, setHasError] = useState(false);

  if (hasError) {
    return <VenuePlaceholderImage className="w-14 h-14 rounded-lg shrink-0" />;
  }

  return (
    <div className="w-14 h-14 rounded-lg overflow-hidden shrink-0">
      <img
        src={src}
        alt=""
        className={`w-full h-full ${!hasVenuePhoto ? "object-contain" : "object-cover"}`}
        loading="lazy"
        onError={() => setHasError(true)}
      />
    </div>
  );
}

interface StackedImagesProps {
  images: string[];
  hasVenuePhoto?: boolean;
}

function StackedImages({ images, hasVenuePhoto = false }: StackedImagesProps) {
  const [erroredIndexes, setErroredIndexes] = useState<Set<number>>(new Set());
  const displayImages = images.slice(0, 2);

  const handleError = (index: number) => {
    setErroredIndexes((prev) => new Set(prev).add(index));
  };

  return (
    <div className="relative w-14 h-14 shrink-0">
      {/* First image - top left */}
      <div className="absolute top-0 left-0 w-10 h-10 rounded-lg overflow-hidden z-10">
        {erroredIndexes.has(0) ? (
          <VenuePlaceholderImage className="w-10 h-10 rounded-lg" />
        ) : (
          <img
            src={displayImages[0]}
            alt=""
            className={`w-full h-full ${!hasVenuePhoto ? "object-contain" : "object-cover"}`}
            loading="lazy"
            onError={() => handleError(0)}
          />
        )}
      </div>
      {/* Second image - bottom right */}
      {displayImages[1] && (
        <div className="absolute bottom-0 right-0 w-10 h-10 rounded-lg overflow-hidden">
          {erroredIndexes.has(1) ? (
            <VenuePlaceholderImage className="w-10 h-10 rounded-lg" />
          ) : (
            <img
              src={displayImages[1]}
              alt=""
              className={`w-full h-full ${!hasVenuePhoto ? "object-contain" : "object-cover"}`}
              loading="lazy"
              onError={() => handleError(1)}
            />
          )}
        </div>
      )}
    </div>
  );
}

export default NotificationItem;
